import React from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneOutlined } from '@mui/icons-material';
import Hearing from '@mui/icons-material/Hearing';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Constants from 'style/constants';
import { InBathroomIcon } from 'shared/components/icons/in-bathroom-icon';
import IntruderIcon from 'shared/components/icons/intruder-icon';
import { OpenDoorIcon } from 'shared/components/icons/open-door-icon';
import OutOfBedIcon from 'shared/components/icons/out-of-bed-icon';
import { OutOfRoomIcon } from 'shared/components/icons/out-of-room-icon';
import SolidPersonFallingIcon from 'shared/components/icons/solid-person-falling-icon';
import SolidPersonWalkingDashedLineArrowRightIcon from 'shared/components/icons/solid-person-walking-dashed-line-arrow-right-icon';
import { ScenarioType } from 'shared/enums/scenario-type';

type Props = {
  isActive?: boolean;
  scenarioType?: ScenarioType;
  sx?: SxProps<Theme>;
  isVisible?: boolean;
};

const OrganizationUnitScenarioIcon = ({ isActive, scenarioType, sx, isVisible }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');

  const visibleColor = !isActive ? Constants.Colors.inactive : Constants.Colors.primary;
  const color = isVisible === undefined || !!isVisible ? visibleColor : 'transparent';

  const getTooltipText = (text: string | undefined) => (isVisible ? text : undefined);

  switch (scenarioType) {
    case ScenarioType.Bed:
      return <OutOfBedIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.Bed'))} />;
    case ScenarioType.Room:
      return <OutOfRoomIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.Room'))} />;
    case ScenarioType.Door:
      return <OpenDoorIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.Door'))} />;
    case ScenarioType.Bathroom:
      return <InBathroomIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.Bathroom'))} />;
    case ScenarioType.Intruder:
      return <IntruderIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.Intruder'))} />;
    case ScenarioType.FallDetection:
      return <SolidPersonFallingIcon sx={sx} color={color} tooltipText={getTooltipText(t('scenarioName.FallDetection'))} />;
    case ScenarioType.Video:
      return (
        <Tooltip title={getTooltipText(t('scenarioName.Video'))}>
          <VideocamOutlined sx={{ ...sx, color }} />
        </Tooltip>
      );
    case ScenarioType.Audio:
      return (
        <Tooltip title={getTooltipText(t('scenarioName.Audio'))}>
          <PhoneOutlined sx={{ ...sx, color }} />
        </Tooltip>
      );
    case ScenarioType.Acoustic:
      return (
        <Tooltip title={getTooltipText(t('scenarioName.Acoustic'))}>
          <Hearing sx={{ ...sx, color }} />
        </Tooltip>
      );
    case ScenarioType.CurrentLocation:
      return (
        <Tooltip title={getTooltipText(t('scenarioName.CurrentLocation'))}>
          <PinDropOutlined sx={{ ...sx, color }} />
        </Tooltip>
      );
    case ScenarioType.WanderingDetection:
      return (
        <SolidPersonWalkingDashedLineArrowRightIcon
          sx={sx}
          color={color}
          tooltipText={getTooltipText(t('scenarioName.WanderingDetection'))}
        />
      );
    default:
      return <></>;
  }
};

const OrganizationUnitBedIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Bed} />
);
OrganizationUnitScenarioIcon.BedIcon = OrganizationUnitBedIcon;

const OrganizationUnitRoomIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Room} />
);
OrganizationUnitScenarioIcon.RoomIcon = OrganizationUnitRoomIcon;

const OrganizationUnitBathroomIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Bathroom} />
);
OrganizationUnitScenarioIcon.BathroomIcon = OrganizationUnitBathroomIcon;

const OrganizationUnitIntruderIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Intruder} />
);
OrganizationUnitScenarioIcon.IntruderIcon = OrganizationUnitIntruderIcon;

const OrganizationUnitFallDetectionIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.FallDetection} />
);
OrganizationUnitScenarioIcon.FallDetectionIcon = OrganizationUnitFallDetectionIcon;

const OrganizationUnitDoorIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Door} />
);
OrganizationUnitScenarioIcon.DoorIcon = OrganizationUnitDoorIcon;

const OrganizationUnitAudioIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Audio} />
);
OrganizationUnitScenarioIcon.AudioIcon = OrganizationUnitAudioIcon;

const OrganizationUnitVideoIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Video} />
);
OrganizationUnitScenarioIcon.VideoIcon = OrganizationUnitVideoIcon;

const OrganizationUnitAcousticIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.Acoustic} />
);
OrganizationUnitScenarioIcon.AcousticIcon = OrganizationUnitAcousticIcon;

const OrganizationUnitCurrentLocationIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.CurrentLocation} />
);
OrganizationUnitScenarioIcon.CurrentLocationIcon = OrganizationUnitCurrentLocationIcon;

const OrganizationUnitWanderingDetectionIcon = (props: Omit<Props, 'scenarioType'>): React.JSX.Element => (
  <OrganizationUnitScenarioIcon {...props} scenarioType={ScenarioType.WanderingDetection} />
);
OrganizationUnitScenarioIcon.WanderingDetectionIcon = OrganizationUnitWanderingDetectionIcon;

export default OrganizationUnitScenarioIcon;
